import { useEffect } from 'react'
import { FullScreenGallery, SvgProvider } from 'react-common'
import sprite from 'react-common/assets/sprite.svg'
import { createRoot } from 'react-dom/client'

const CLASSES = {
  container: 'gallery-container',
  arrow: 'gallery-arrow',
  arrowLeft: 'gallery-arrow_left',
  arrowRight: 'gallery-arrow_right',
  image: 'gallery-item',
  imageActive: 'gallery-item_active',
  dot: 'gallery-dot',
  dotActive: 'gallery-dot_active',
}

type Cleaner = () => void

export const useHandleGallery = (content: string) => {
  useEffect(() => {
    const listenerCleaners: Cleaner[] = []

    const galleries = document.querySelectorAll(`.${CLASSES.container}`)

    galleries.forEach((gallery) => {
      const images = gallery.querySelectorAll(`.${CLASSES.image}`)
      const arrows = gallery.querySelectorAll(`.${CLASSES.arrow}`)
      const arrowLeft = gallery.querySelector(`.${CLASSES.arrowLeft}`)
      const arrowRight = gallery.querySelector(`.${CLASSES.arrowRight}`)
      const dots = gallery.querySelectorAll(`.${CLASSES.dot}`)

      const handleNavigate = (newIndex: number) => {
        // handle preview
        images.forEach((image, index) => {
          image.classList.toggle(CLASSES.imageActive, index === newIndex)
        })

        // handle arrow state
        arrowLeft?.querySelector('button')?.removeAttribute('disabled')
        arrowRight?.querySelector('button')?.removeAttribute('disabled')
        if (newIndex <= 0) {
          arrowLeft?.querySelector('button')?.setAttribute('disabled', 'true')
        } else if (newIndex >= images.length - 1) {
          arrowRight?.querySelector('button')?.setAttribute('disabled', 'true')
        }

        // handle dot state
        dots.forEach((dot, index) => {
          dot.classList.toggle(CLASSES.dotActive, index === newIndex)
        })
      }

      arrows.forEach((arrow) => {
        const handleArrowClick = () => {
          const direction = arrow === arrowLeft ? -1 : 1
          const currentIndex = Array.from(images).findIndex((image) => image.classList.contains(CLASSES.imageActive))
          const newIndex = currentIndex + direction

          if (newIndex < 0 || newIndex >= images.length) {
            return
          }

          handleNavigate(newIndex)
        }

        arrow.addEventListener('click', handleArrowClick)
        listenerCleaners.push(() => arrow.removeEventListener('click', handleArrowClick))
      })

      dots.forEach((dot, index) => {
        const handleDotClick = () => {
          handleNavigate(index)
        }
        dot.addEventListener('click', handleDotClick)
        listenerCleaners.push(() => dot.removeEventListener('click', handleDotClick))
      })

      const imgs = gallery.querySelectorAll(`.${CLASSES.image} img`)
      const handleImageClick = () => {
        const div = document.createElement('div')
        const root = createRoot(div)

        root.render(
          <SvgProvider filename={sprite}>
            <FullScreenGallery
              images={Array.from(imgs).map((img) => ({
                src: img.getAttribute('src') || '',
                name: img.getAttribute('alt') || '',
              }))}
              activeIndex={Array.from(images).findIndex((image) => image.classList.contains(CLASSES.imageActive))}
              onClose={() => {
                root.unmount()
                div.remove()
              }}
            />
          </SvgProvider>,
        )

        document.body.appendChild(div)

        listenerCleaners.push(() => {
          root.unmount()
          div.remove()
        })
      }

      imgs.forEach((img) => {
        img.addEventListener('click', handleImageClick)
        listenerCleaners.push(() => img.removeEventListener('click', handleImageClick))
      })
    })

    return () => {
      listenerCleaners.forEach((cleaner) => cleaner())
    }
  }, [content])
}
